import React from "react";
import styled, { css } from "styled-components";
import { Icon } from "./Icon";
import { style } from "./style";
import { focusOutline, linkFocusOutline } from "./utils/focusOutlineStyle";

const baseButtonStyles = css<StyledProps & IconProps>`
  appearance: none;
  margin: 0;
  overflow: visible;
  font: inherit;
  font-weight: ${style.vars.fontWeight.bold};
  color: ${({ variation, active }) => {
    if (variation === "secondary" && !active) {
      return style.colors.blue.dark;
    } else if (variation === "tertiary") {
      if (active) {
        return style.colors.blue.darkest;
      }
      return style.colors.blue.dark;
    } else if (variation === "danger-secondary" && !active) {
      return style.colors.red.dark;
    } else if (variation === "list-item") {
      if (active) {
        return style.colors.blue.darkest;
      }
      return style.colors.blue.dark;
    } else if (variation === "banner-secondary") {
      if (active) {
        return style.colors.grey.dark;
      }
      return style.colors.grey.darkest;
    } else {
      return style.colors.white;
    }
  }};
  text-transform: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0
    ${({ compact, $iconOnly, variation }) =>
      variation === "tertiary" || variation === "banner-secondary" || $iconOnly
        ? 0
        : compact
        ? style.vars.space["2"]
        : style.vars.space["3"]};
  background: ${({ variation, active }) => {
    if (!variation || variation === "primary") {
      if (active) {
        return style.colors.blue.darkest;
      }
      return style.colors.blue.dark;
    } else if (variation === "secondary") {
      if (active) {
        return style.colors.blue.darkest;
      }
      return style.colors.white;
    } else if (variation === "tertiary") {
      return "transparent";
    } else if (variation === "success") {
      if (active) {
        return style.colors.green.darkest;
      }
      return style.colors.green.dark;
    } else if (variation === "danger") {
      if (active) {
        return style.colors.red.darkest;
      }
      return style.colors.red.dark;
    } else if (variation === "danger-secondary") {
      if (active) {
        return style.colors.red.dark;
      }
      return style.colors.white;
    } else if (variation === "list-item") {
      return "transparent";
    } else if (variation === "banner") {
      if (active) {
        return style.colors.grey.dark;
      }
      return style.colors.grey.darkest;
    } else if (variation === "banner-secondary") {
      return "transparent";
    } else if (variation === "icon") {
      return "transparent";
    }
  }};

  vertical-align: middle;
  cursor: pointer;
  border-radius: ${style.vars.borderRadius.base};
  transition: all ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};
  line-height: ${({ variation, compact }) => (variation === "list-item" ? 1 : compact ? "36px" : style.vars.height.button)};
  height: ${({ variation, compact, $iconOnly }) =>
    $iconOnly ? style.vars.height.button : variation === "list-item" ? "32px" : compact ? style.vars.height.small : style.vars.height.base};
  min-width: ${({ $iconOnly }) => ($iconOnly ? style.vars.height.button : style.vars.height.small)};
  max-width: ${({ $iconOnly }) => ($iconOnly ? style.vars.height.button : "none")};
  font-size: ${style.vars.fontSize.base};
  text-decoration: ${({ variation, active }) =>
    (variation === "tertiary" || variation === "banner-secondary") && active ? "underline" : "none"};
  text-align: center;
  white-space: nowrap;
  border-width: ${({ variation }) => (variation === "tertiary" || variation === "banner-secondary" ? 0 : style.vars.borderWidth.base)};
  border-style: solid;
  border-color: ${({ variation, active }) => {
    if (variation === "secondary") {
      if (active) {
        return style.colors.blue.darkest;
      }
      return style.colors.blue.dark;
    } else if (variation === "danger-secondary") {
      return style.colors.red.dark;
    } else {
      return "transparent";
    }
  }};

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  &:focus {
    ${focusOutline({ margin: "-1px" })};
    color: ${({ variation, active }) => {
      if (variation === "secondary" && !active) {
        return style.colors.blue.dark;
      } else if (variation === "tertiary") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.dark;
      } else if (variation === "danger-secondary" && !active) {
        return style.colors.red.dark;
      } else if (variation === "list-item") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.dark;
      } else if (variation === "banner-secondary") {
        if (active) {
          return style.colors.grey.dark;
        }
        return style.colors.grey.darkest;
      } else {
        return style.colors.white;
      }
    }};

    ${({ variation, active }) => {
      if (variation === "banner") {
        return `background: ${active ? style.colors.grey.dark : style.colors.grey.darkest}`;
      }
    }};
  }

  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }

  &:hover {
    color: ${({ variation, active }) => {
      if (variation === "tertiary") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.primary;
      } else if (variation === "list-item") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.primary;
      } else if (variation === "banner-secondary") {
        return style.colors.grey.dark;
      }
      return style.colors.white;
    }};
    background: ${({ variation, active }) => {
      if (!variation || variation === "primary") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.primary;
      } else if (variation === "secondary") {
        if (active) {
          return style.colors.blue.darkest;
        }
        return style.colors.blue.dark;
      } else if (variation === "success") {
        if (active) {
          return style.colors.green.darkest;
        }
        return style.colors.green.primary;
      } else if (variation === "danger") {
        if (active) {
          return style.colors.red.darkest;
        }
        return style.colors.red.primary;
      } else if (variation === "danger-secondary") {
        return style.colors.red.dark;
      } else if (variation === "banner") {
        if (active) {
          return style.colors.grey.dark;
        }
        return style.colors.grey.primary;
      } else if (variation === "banner-secondary") {
        return "transparent";
      } else if (variation === "icon") {
        if (active) {
          return "transparent";
        }
        return style.colors.grey.lightest;
      }
    }};

    ${({ variation, active }) =>
      variation === "list-item" &&
      css`
        &::before {
          background: ${active ? style.colors.blue.darkest : style.colors.blue.primary};
        }
      `}

    ${({ variation }) =>
      (variation === "tertiary" || variation === "banner-secondary") &&
      css`
        text-decoration: underline;
      `}
  }
  ${({ variation, active }) =>
    variation === "list-item" &&
    css`
      &::before {
        content: "";
        width: 24px;
        height: 24px;
        background: ${active ? style.colors.blue.darkest : style.colors.blue.dark};
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 1px;
        border-radius: 2px;
        transition: background ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};
      }
    `}
`;

const ICON_SIZE = "10px";
const ICON_ONLY_SIZE = "14px";

const IconLeft = styled(Icon)<IconProps>`
  margin-right: ${style.vars.space["2"]};
  height: ${({ $finalIconSize }) => $finalIconSize};
  width: ${({ $finalIconSize }) => $finalIconSize};
  position: relative;
  top: -1px;

  ${({ $listItem }) =>
    $listItem &&
    css`
      margin-right: ${style.vars.space["3"]};
      fill: ${style.colors.white};
    `}

  ${({ $iconOnly, $active }) =>
    $iconOnly &&
    css`
      margin-right: 0;
      fill: ${$active ? style.colors.grey.darkest : style.colors.grey.dark};
    `}
`;

export const IconRight = styled(IconLeft)<IconProps>`
  margin: 0 0 0 ${style.vars.space["2"]};
`;

const ButtonElt = styled.button`
  ${baseButtonStyles};
`;

const AnchorElt = styled.a`
  ${baseButtonStyles};
`;

type IconSizes = "small" | "large" | "xlarge" | (string & {});

type IconProps = {
  $listItem?: boolean;
  $finalIconSize?: string;
  $iconOnly?: boolean;
  $active?: boolean;
};

export type ButtonVariation =
  | "primary"
  | "secondary"
  | "tertiary"
  | "icon"
  | "success"
  | "danger"
  | "danger-secondary"
  | "list-item"
  | "banner"
  | "banner-secondary";

export type StyledProps = {
  variation?: ButtonVariation;
  compact?: boolean;
  active?: boolean;
  fullWidth?: boolean;
};

export type ButtonProps = StyledProps &
  React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> & {
    url?: string;
    external?: boolean;
    icon?: string;
    iconSize?: IconSizes;
    iconRight?: boolean;
    disabled?: boolean;
    className?: string;
    type?: "button" | "submit" | "reset";
    onClick?: React.MouseEventHandler<HTMLElement>;

    /**
     * Attribute used by Pendo and the <AnalyticsContext> to track events
     */
    "data-tracking-id"?: string;
    /**
     * Attribute used by testing libraries to identify the component
     */
    "data-testid"?: string;
  };

const DEFAULT_VARIATION = "primary";

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  ({ type = "button", external, url, children, iconSize, iconRight = false, ...props }, ref) => {
    props.variation = props.variation ? props.variation : DEFAULT_VARIATION;
    const target = external ? "_blank" : undefined;
    const rel = external ? "noopener noreferrer" : undefined;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const iconOnly = (props.icon && !children) || props.variation === "icon";
    const isListItem = props.variation === "list-item";
    props.compact = iconOnly || isListItem ? true : props.compact;
    const hasIconSizeInStyles = !["small", "large", "xlarge"].includes(iconSize as IconSizes);
    const finalIconSize = !hasIconSizeInStyles ? null : iconOnly ? iconSize || ICON_ONLY_SIZE : iconSize || ICON_SIZE;

    const iconProps: any = {
      icon: props.icon,
      $iconOnly: iconOnly,
      $listItem: isListItem,
      $active: props.active,
      $finalIconSize: finalIconSize,
    };

    if (iconSize !== undefined) {
      iconProps[iconSize] = true;
    }

    return url ? (
      <AnchorElt
        href={url}
        target={target}
        rel={rel}
        $iconOnly={iconOnly}
        ref={ref as React.MutableRefObject<HTMLAnchorElement>}
        {...(iconOnly && { "aria-label": `${props.icon}` })}
        {...props}
      >
        <span>
          {iconProps.icon !== undefined && !iconRight && <IconLeft {...iconProps} />}
          {!iconOnly && children}
          {iconProps.icon !== undefined && iconRight && <IconRight {...iconProps} />}
        </span>
      </AnchorElt>
    ) : (
      <ButtonElt
        type={type}
        $iconOnly={iconOnly}
        ref={ref as React.MutableRefObject<HTMLButtonElement>}
        {...(iconOnly && { "aria-label": `${props.icon}` })}
        {...props}
      >
        {iconProps.icon !== undefined && !iconRight && <IconLeft {...iconProps} />}
        {!iconOnly && children}
        {iconProps.icon !== undefined && iconRight && <IconRight {...iconProps} />}
      </ButtonElt>
    );
  }
);

/**
 * InlineButton looks like a link, but uses onClick instead of
 * a href and can be used inline in a paragraph.
 */
export const InlineButton = styled.span.attrs({
  role: "button",
  tabIndex: 0,
})`
  color: ${style.colors.primary.base};
  text-decoration: none;
  cursor: pointer;

  ${linkFocusOutline()}

  &&:hover,
  &&:focus {
    color: ${style.colors.primary.hover};
    text-decoration: underline;
  }
`;
